import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import BASE_URL, {
  AUTH0_AUDIENCE,
  AUTH0_CONFIGURED,
  getAuth0RedirectUri,
} from "./config";
import { addUser } from "./store/slices/UserSlice";
import {
  normalizeUserableType,
  USERABLE_EMPLOYER,
  USERABLE_JOB_SEEKER,
} from "./auth0UserableType";

function readAuth0ExchangeMeta() {
  try {
    const raw = sessionStorage.getItem("auth0_exchange_meta");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/** Completes login: Auth0 JWT → Laravel Passport token (same shape as /login). */
export default function Auth0Exchange() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims } =
    useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [fatal, setFatal] = useState(null);
  const [serverBusy, setServerBusy] = useState(false);

  useEffect(() => {
    if (!AUTH0_CONFIGURED) {
      navigate("/", { replace: true });
      return;
    }

    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setServerBusy(false);
      navigate("/login", { replace: true });
      return;
    }

    const run = async () => {
      setServerBusy(true);
      try {
        const tokenResponse = await getAccessTokenSilently({
          authorizationParams: {
            audience: AUTH0_AUDIENCE,
            redirect_uri: getAuth0RedirectUri(),
          },
          detailedResponse: true,
        });
        const token = tokenResponse?.access_token;
        if (!token) {
          throw new Error("Missing Auth0 access token");
        }

        const meta = readAuth0ExchangeMeta();

        const rawPortal =
          location.state?.exchangeUserableType ?? meta?.exchangeUserableType;
        const hasExplicitPortal =
          rawPortal != null && String(rawPortal).trim() !== "";
        const rememberMe =
          !!location.state?.rememberMe || !!meta?.rememberMe;
        const preLogin = localStorage.getItem("login");

        const exchangeBody = hasExplicitPortal
          ? {
              userable_type: normalizeUserableType(rawPortal),
            }
          : {};

        let idTokenJwt = tokenResponse?.id_token;
        if (!idTokenJwt) {
          try {
            const idClaims = await getIdTokenClaims();
            if (idClaims && idClaims.__raw) {
              idTokenJwt = idClaims.__raw;
            }
          } catch (_) {
            /* optional — exchange can still succeed if email is on access token / userinfo */
          }
        }
        if (idTokenJwt) {
          exchangeBody.id_token = idTokenJwt;
        }

        const res = await axios.post(
          `${BASE_URL}/auth/auth0/exchange`,
          exchangeBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        try {
          sessionStorage.removeItem("auth0_exchange_meta");
        } catch (_) {
          /* ignore */
        }

        if (res.data.status !== 200) {
          throw new Error(res.data.msg || "Auth failed");
        }

        const auth0NewUser = !!res.data.auth0_new_user;

        localStorage.clear();
        const user = res.data.user;
        const userable_type = user.userable_type;

        if (userable_type === USERABLE_JOB_SEEKER) {
          localStorage.setItem("jobSeekerId", user.userable_id);
        }
        if (userable_type === USERABLE_EMPLOYER) {
          localStorage.setItem("employerId", user.userable_id);
        }
        localStorage.setItem("userId", user.id);
        localStorage.setItem("access_token", res.data.access_token);
        localStorage.setItem("userable_type", userable_type);

        const expiryDate = new Date();
        if (rememberMe) {
          expiryDate.setDate(expiryDate.getDate() + 30);
        } else {
          expiryDate.setDate(expiryDate.getDate() + 1);
        }
        localStorage.setItem("expiry_date", expiryDate.toISOString());

        await dispatch(addUser(res.data.user));

        if (userable_type === USERABLE_JOB_SEEKER) {
          if (auth0NewUser || preLogin === "0") {
            localStorage.setItem("login", "0");
          }
          if (localStorage.getItem("login") === "0") {
            navigate("/login-attempt", { replace: true });
          } else {
            navigate("/job_seeker_dashboard", { replace: true });
          }
          return;
        }

        navigate("/employer_dashboard", { replace: true });
      } catch (e) {
        setServerBusy(false);
        const status = e?.response?.status;
        const data = e?.response?.data;
        const firstValidation =
          data?.errors &&
          Object.values(data.errors)
            .flat()
            .filter(Boolean)[0];
        let msg =
          data?.msg ||
          data?.message ||
          firstValidation ||
          e.message ||
          "Unable to finish sign-in. Try email/password or check Auth0 and API audience settings.";
        if (status === 404) {
          msg =
            "Auth0 exchange endpoint was not found (404). Deploy the latest Rezmaze-BE to this server " +
            "(route POST /api/auth/auth0/exchange), run migrations for `auth0_sub`, then try again. " +
            "Or point REACT_APP_BASE_URL at a backend that includes the Auth0 changes.";
        }
        if (data?.code === "AUTH0_PORTAL_MISMATCH") {
          msg = data.msg || "This email is already registered.";
        }
        if (!e?.response && /network error/i.test(String(e?.message || ""))) {
          msg = "This email is already registered.";
        }
        if (status === 422 && data?.errors && !data?.msg) {
          msg = `${msg}${firstValidation ? ` (${firstValidation})` : ""}`;
        }
        setFatal(msg);
      }
    };

    run();
  }, [
    AUTH0_CONFIGURED,
    dispatch,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    location.state,
    navigate,
  ]);

  if (fatal) {
    return (
      <div className="container py-5 text-center">
        <p className="text-danger">{fatal}</p>
        <button
          type="button"
          onClick={() => {
            navigate("/login", { replace: true });
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  const spinnerOverlay = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f6f9",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          border: "3px solid #e2e6ea",
          borderTopColor: "#635bff",
          borderRadius: "50%",
          animation: "rezmazeAuthSpin 0.75s linear infinite",
        }}
      />
      <style>{`
        @keyframes rezmazeAuthSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );

  return spinnerOverlay;
}
